import type {
  Codespace,
  FetchCodespaceListParams
} from 'Services/ControlDataManagement/Features/Codespace/types'
import type { ListResponse } from 'Services/ControlDataManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  CODESPACE = 'Codespace'
}

export const codespaceApi = createBaseApi({
  reducerPath: 'codespaceApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.CODESPACE]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getCodespaces: builder.query<ListResponse<Codespace>, FetchCodespaceListParams | void>({
      query: params => ({
        url: '/api/v1/codespace/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ codespace_id }) => ({ type: TagTypes.CODESPACE, id: codespace_id }) as const
              ),
              { type: TagTypes.CODESPACE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.CODESPACE, id: RTKCacheTags.LIST }]
    }),
    getSingleCodespace: builder.query<Codespace, string>({
      query: codespace_id => `/api/v1/codespace/${codespace_id}/`,
      providesTags: (_result, _error, codespace_id) => [
        { type: TagTypes.CODESPACE, id: codespace_id }
      ]
    }),
    createSingleCodespace: builder.mutation<Codespace, Partial<Codespace>>({
      query: data => ({
        url: `/api/v1/codespace/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.CODESPACE, id: RTKCacheTags.LIST }]
    }),
    updateSingleCodespace: builder.mutation<
      Codespace,
      { codespace_id: string } & Partial<Codespace>
    >({
      query: ({ codespace_id, ...data }) => ({
        url: `/api/v1/codespace/${codespace_id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { codespace_id }) => [
        { type: TagTypes.CODESPACE, id: codespace_id }
      ]
    }),
    deleteSingleCodespace: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/codespace/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: TagTypes.CODESPACE, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetCodespacesQuery,
  useLazyGetCodespacesQuery,
  useGetSingleCodespaceQuery,
  useCreateSingleCodespaceMutation,
  useUpdateSingleCodespaceMutation,
  useDeleteSingleCodespaceMutation
} = codespaceApi
